import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatDate, formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { RotateCw, Send, CheckCircle2, XCircle, Clock, Wallet } from "lucide-react";

export default function Notifications() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [l, s] = await Promise.all([api.get("/notifications"), api.get("/notifications/stats")]);
    setList(l.data);
    setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const retry = async (id) => {
    try { const { data } = await api.post(`/notifications/${id}/retry`); toast[data.status === "Sent" ? "success" : "info"](`Retry: ${data.status}${data.detail ? " — " + data.detail : ""}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const retryAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/notifications/retry-failed`);
      if (data.retried === 0) toast.info(data.detail || "Nothing to retry");
      else if (data.sent === data.retried) toast.success(`Sent ${data.sent} of ${data.retried} notifications`);
      else toast.info(`Retried ${data.retried} · Sent ${data.sent} · Still Failed ${data.still_failed}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const failedCount = stats?.by_status?.Failed || 0;
  const monthLabel = stats ? new Date(stats.month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "";

  return <div className="space-y-6" data-testid="notifications-page">
    <div className="flex items-start justify-between flex-wrap gap-4">
      <div>
        <div className="overline">Outbound</div>
        <h1 className="font-serif text-4xl mt-1">Notification Log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every advance, repayment and settlement event queues a WhatsApp to the employee via Interakt.
          Failed rows can be retried individually or in bulk.
        </p>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button className="gold-bg" disabled={busy || failedCount === 0} data-testid="retry-all-failed-btn">
            <Send className="w-4 h-4 mr-2" />
            {busy ? "Sending…" : `Send now (${failedCount})`}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retry {failedCount} failed notifications?</AlertDialogTitle>
            <AlertDialogDescription>
              Every Failed row will be re-sent via Interakt using each employee's latest phone number and current outstanding balance.
              Successful sends will flip to <b className="text-green-500">Sent</b>. Failed ones stay in the log for another retry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={retryAll} data-testid="confirm-retry-all-btn">Retry all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>

    {stats && <div className="space-y-2" data-testid="notification-stats">
      <div className="overline">{monthLabel} — Usage</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Sent" value={stats.by_status.Sent || 0} icon={<CheckCircle2 className="w-5 h-5" />} color="text-green-400" testid="stat-sent" />
        <StatCard label="Failed" value={stats.by_status.Failed || 0} icon={<XCircle className="w-5 h-5" />} color="text-red-400" testid="stat-failed" />
        <StatCard label="Pending" value={stats.by_status.Pending || 0} icon={<Clock className="w-5 h-5" />} color="text-amber-400" testid="stat-pending" />
        <StatCard
          label="Estimated Cost"
          value={formatINR(stats.estimated_cost_inr)}
          icon={<Wallet className="w-5 h-5" />}
          color="gold"
          testid="stat-cost"
          sub={`@ ${formatINR(stats.cost_rate_inr)} per sent message`}
        />
      </div>
    </div>}

    <div className="border rounded-md overflow-x-auto"><table className="plutus"><thead><tr>
      <th>Date</th><th>Employee</th><th>Phone</th><th>Type</th><th>Status</th><th>Message</th><th></th>
    </tr></thead><tbody data-testid="notif-list">
      {list.map(n => <tr key={n.id}>
        <td className="font-mono text-xs">{formatDate(n.created_at)}</td>
        <td><span className="font-mono text-xs text-muted-foreground mr-2">{n.employee_code}</span>{n.employee_name}</td>
        <td className="font-mono text-xs">{n.phone}</td>
        <td>{n.notification_type}</td>
        <td>
          <span className={`pill pill-${n.status.toLowerCase()}`}>{n.status}</span>
          {n.error && n.status === "Failed" && <div className="text-[10px] text-red-400/80 mt-1 max-w-xs truncate" title={n.error}>{n.error}</div>}
        </td>
        <td className="text-xs text-muted-foreground max-w-md truncate">{n.message}</td>
        <td className="text-right">{n.status !== "Sent" && <Button size="sm" variant="ghost" onClick={() => retry(n.id)} data-testid={`retry-${n.id}`}><RotateCw className="w-3 h-3 mr-1" />Retry</Button>}</td>
      </tr>)}
      {list.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No notifications yet.</td></tr>}
    </tbody></table></div>
  </div>;
}

function StatCard({ label, value, icon, color, testid, sub }) {
  return <div className="kpi-card" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="overline">{label}</div>
        <div className={`font-serif text-3xl mt-2 tabular-nums ${color}`}>{value}</div>
        {sub && <div className="text-[10px] text-muted-foreground mt-2">{sub}</div>}
      </div>
      <div className={color}>{icon}</div>
    </div>
  </div>;
}
